// src/pages/PartnersPage.tsx
import React, { useState } from "react";
import {
  CheckCircle, Circle, Upload, Database, Shield, Zap,
  Building2, ChevronRight, Check, AlertTriangle, RefreshCw,
  FileText, Key, BookOpen, HeadphonesIcon, TrendingUp,
} from "lucide-react";
import { clsx } from "clsx";
import { RETAILERS, LOYALTY_SUMMARIES } from "@/data";

type Tab = "overview" | "onboarding" | "health" | "api";

// ── Onboarding steps ──────────────────────────────────────────
const ONBOARD_STEPS = [
  { id: 1, label: "Agreement",       desc: "Data sharing agreement executed",          icon: FileText   },
  { id: 2, label: "Data Ingestion",  desc: "Upload loyalty/CRM/transaction data",      icon: Upload     },
  { id: 3, label: "Schema Mapping",  desc: "Map data fields to MRN canonical schema",  icon: Database   },
  { id: 4, label: "Match & Enrich",  desc: "Identity resolution and intent overlay",   icon: Zap        },
  { id: 5, label: "Activate",        desc: "Audiences ready for deployment",           icon: CheckCircle },
];

// ── Partner status ────────────────────────────────────────────
const PARTNER_STATUS = [
  { id: "org-mhw", name: "Midwest Hardware",  step: 5, matchRate: 92, records: 2_840_000, lastSync: "2h ago",   status: "active"  },
  { id: "org-fmg", name: "FreshMart Grocery", step: 5, matchRate: 89, records: 5_120_000, lastSync: "1h ago",   status: "active"  },
  { id: "org-sna", name: "StyleNow Apparel",  step: 5, matchRate: 87, records: 1_670_000, lastSync: "3h ago",   status: "active"  },
  { id: "org-new", name: "AutoZone Parts",    step: 3, matchRate: 0,  records: 0,          lastSync: "Pending",  status: "pending" },
];

// ── API endpoints ─────────────────────────────────────────────
const API_ENDPOINTS = [
  { method: "POST", path: "/v1/identity/resolve",       desc: "Resolve identifiers to MRN Person ID",        category: "Identity"   },
  { method: "POST", path: "/v1/identity/batch-resolve", desc: "Bulk resolution up to 10M records",           category: "Identity"   },
  { method: "GET",  path: "/v1/identity/{id}/graph",    desc: "Get all linked identifiers for a person",     category: "Identity"   },
  { method: "POST", path: "/v1/audiences",              desc: "Create audience segment",                     category: "Audiences"  },
  { method: "POST", path: "/v1/audiences/{id}/activate",desc: "Deploy audience to channels",                 category: "Audiences"  },
  { method: "GET",  path: "/v1/audiences/{id}/health",  desc: "Size trends and freshness status",            category: "Audiences"  },
  { method: "POST", path: "/v1/campaigns",              desc: "Create campaign",                             category: "Campaigns"  },
  { method: "GET",  path: "/v1/campaigns/{id}/performance", desc: "Real-time campaign metrics",              category: "Campaigns"  },
  { method: "GET",  path: "/v1/campaigns/{id}/attribution", desc: "Attribution report",                      category: "Campaigns"  },
  { method: "GET",  path: "/v1/intent/topics",          desc: "Browse intent topics with audience sizes",    category: "Intelligence"},
  { method: "POST", path: "/v1/loyalty/enrich",         desc: "Enrich loyalty file with intent signals",     category: "Intelligence"},
  { method: "GET",  path: "/v1/loyalty/{id}/lapse-alerts", desc: "Lapse risk alerts for retailer",          category: "Intelligence"},
  { method: "POST", path: "/v1/catalog/ingest",         desc: "Upload product catalog (up to 5M SKUs)",      category: "Catalog"    },
  { method: "GET",  path: "/v1/catalog/{id}/trending",  desc: "Real-time trending products by intent surge", category: "Catalog"    },
];

const METHOD_COLORS: Record<string, string> = {
  GET:    "bg-emerald-50 text-emerald-700",
  POST:   "bg-blue/10 text-blue",
  PATCH:  "bg-amber-50 text-amber-700",
  DELETE: "bg-red-50 text-red-600",
};

// ── Data health metrics ───────────────────────────────────────
const HEALTH_METRICS = [
  { retailer: "Midwest Hardware",  matchRate: 92, freshness: 98, completeness: 94, records: 2_840_000, issues: 0   },
  { retailer: "FreshMart Grocery", matchRate: 89, freshness: 99, completeness: 91, records: 5_120_000, issues: 2   },
  { retailer: "StyleNow Apparel",  matchRate: 87, freshness: 96, completeness: 88, records: 1_670_000, issues: 1   },
];

export default function PartnersPage() {
  const [tab, setTab]               = useState<Tab>("overview");
  const [onboardStep, setOnboardStep] = useState(3);
  const [apiCategory, setApiCategory] = useState("All");
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);

  const filteredEndpoints = API_ENDPOINTS.filter(e =>
    apiCategory === "All" || e.category === apiCategory
  );

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="page-header mb-0">
          <h1 className="page-title">Partner Portal</h1>
          <p className="page-subtitle">Onboarding, data health, clean room access, and API documentation</p>
        </div>
        <button className="btn-primary flex items-center gap-2">
          <Building2 size={14} /> Add Partner
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {([
          { id: "overview",   label: "Partners Overview" },
          { id: "onboarding", label: "Onboarding Wizard" },
          { id: "health",     label: "Data Health"       },
          { id: "api",        label: "API Docs"          },
        ] as { id: Tab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={clsx("px-4 py-2.5 text-sm font-medium border-b-2 transition-all duration-150 -mb-px",
              tab === t.id ? "border-blue text-blue" : "border-transparent text-slate hover:text-navy")}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && (
        <div className="space-y-4">
          {/* Summary KPIs */}
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: "Active Partners",    value: "3",     sub: "Fully onboarded"           },
              { label: "Total Records",      value: "9.6M",  sub: "Across all partners"       },
              { label: "Avg Match Rate",     value: "89.3%", sub: "Post-graph resolution"     },
              { label: "Pending Onboarding", value: "1",     sub: "AutoZone Parts — Step 3/5" },
            ].map((kpi, i) => (
              <div key={i} className="kpi-card">
                <p className="kpi-label">{kpi.label}</p>
                <p className="kpi-value">{kpi.value}</p>
                <p className="text-xs text-slate mt-0.5">{kpi.sub}</p>
              </div>
            ))}
          </div>

          {/* Partner cards */}
          <div className="grid grid-cols-2 gap-4">
            {PARTNER_STATUS.map(partner => {
              const summary = LOYALTY_SUMMARIES.find(s => s.retailerId === partner.id);
              return (
                <div key={partner.id}
                  onClick={() => setSelectedPartner(partner.id === selectedPartner ? null : partner.id)}
                  className={clsx("card cursor-pointer transition-all duration-150",
                    selectedPartner === partner.id ? "ring-2 ring-blue" : "hover:shadow-card-hover")}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-navy/10 flex items-center justify-center">
                        <Building2 size={18} className="text-navy" />
                      </div>
                      <div>
                        <p className="font-semibold text-navy">{partner.name}</p>
                        <p className="text-xs text-slate">Last sync: {partner.lastSync}</p>
                      </div>
                    </div>
                    <span className={clsx("badge", partner.status === "active" ? "badge-green" : "badge-yellow")}>
                      {partner.status === "active" ? "Active" : "Onboarding"}
                    </span>
                  </div>

                  {/* Onboarding progress */}
                  <div className="flex items-center gap-1 mb-3">
                    {ONBOARD_STEPS.map((step, i) => (
                      <React.Fragment key={step.id}>
                        <div className={clsx("w-6 h-6 rounded-full flex items-center justify-center text-2xs font-bold flex-shrink-0",
                          partner.step >= step.id ? "bg-emerald-500 text-white" : "bg-gray-100 text-gray-400")}>
                          {partner.step >= step.id ? <Check size={10} /> : step.id}
                        </div>
                        {i < ONBOARD_STEPS.length - 1 && (
                          <div className={clsx("flex-1 h-0.5", partner.step > step.id ? "bg-emerald-500" : "bg-gray-100")} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  {partner.status === "active" ? (
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: "Records",    value: (partner.records/1_000_000).toFixed(1)+"M" },
                        { label: "Match Rate", value: partner.matchRate+"%"                       },
                        { label: "Enriched",   value: summary ? (summary.enrichedMembers/1_000_000).toFixed(1)+"M" : "--" },
                      ].map((m, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="text-sm font-bold text-navy tabular">{m.value}</p>
                          <p className="text-2xs text-slate">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-center gap-2">
                      <AlertTriangle size={13} className="text-amber-500 flex-shrink-0" />
                      <p className="text-xs text-amber-700">Schema mapping in progress — Step 3 of 5</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Clean room access */}
          <div className="card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-navy" />
                <h3 className="text-sm font-semibold text-navy">Clean Room Access</h3>
              </div>
              <span className="badge badge-cyan">Snowflake</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {RETAILERS.map(r => (
                <div key={r.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-800">{r.name}</p>
                    <span className="badge badge-green text-2xs">Active</span>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { label: "Partition",   value: r.id.toUpperCase()  },
                      { label: "Match Rate",  value: r.matchRate + "%"   },
                      { label: "Queries/day", value: "24"                },
                    ].map((m, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-2xs text-slate">{m.label}</span>
                        <span className="text-2xs font-medium text-gray-800 tabular">{m.value}</span>
                      </div>
                    ))}
                  </div>
                  <button className="btn-secondary btn-sm w-full mt-2 text-2xs">Open Clean Room</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Onboarding Wizard ── */}
      {tab === "onboarding" && (
        <div className="max-w-2xl mx-auto space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-navy">AutoZone Parts — Onboarding</h3>
              <span className="badge badge-yellow">Step {onboardStep} of 5</span>
            </div>
            <p className="text-xs text-slate mb-4">Complete all steps to activate data collaboration</p>

            {/* Step indicators */}
            <div className="flex items-center gap-2 mb-6">
              {ONBOARD_STEPS.map((step, i) => (
                <React.Fragment key={step.id}>
                  <button onClick={() => setOnboardStep(step.id)}
                    className={clsx("flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all",
                      onboardStep === step.id ? "bg-blue text-white" :
                      step.id < onboardStep   ? "bg-emerald-50 text-emerald-700 cursor-pointer" :
                      "text-slate cursor-default bg-gray-50")}>
                    {step.id < onboardStep ? <Check size={11} /> : <step.icon size={11} />}
                    {step.label}
                  </button>
                  {i < ONBOARD_STEPS.length - 1 && <ChevronRight size={13} className="text-gray-200 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>

            {/* Step content */}
            {onboardStep === 1 && (
              <div className="space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle size={16} className="text-emerald-500" />
                  <p className="text-xs font-medium text-emerald-700">Data sharing agreement executed — March 1, 2026</p>
                </div>
                <div className="space-y-2">
                  {["Data Usage Agreement", "Privacy Terms (CCPA)", "Clean Room SLA", "Attribution License"].map((doc, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50">
                      <div className="flex items-center gap-2">
                        <FileText size={13} className="text-slate" />
                        <span className="text-xs text-gray-800">{doc}</span>
                      </div>
                      <span className="badge badge-green text-2xs">Signed</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {onboardStep === 2 && (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-blue/40 hover:bg-blue/5 transition-all cursor-pointer">
                  <Upload size={28} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Drop your loyalty file here</p>
                  <p className="text-xs text-slate mt-1">CSV, JSON, or Parquet · Up to 10M records</p>
                  <button className="btn-primary btn-sm mt-4">Browse Files</button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {["Loyalty/CRM Data", "Transaction History", "Product Catalog"].map((type, i) => (
                    <div key={i} className={clsx("border-2 rounded-lg p-3 text-center cursor-pointer transition-all",
                      i === 0 ? "border-blue bg-blue/5" : "border-gray-200 hover:border-gray-300")}>
                      <Database size={16} className={clsx("mx-auto mb-1", i === 0 ? "text-blue" : "text-gray-400")} />
                      <p className="text-xs font-medium text-gray-700">{type}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {onboardStep === 3 && (
              <div className="space-y-3">
                <p className="text-xs text-slate">Map your data fields to MRN canonical schema</p>
                <div className="space-y-2">
                  {[
                    { source: "customer_id",    target: "loyalty_member_id", status: "mapped"   },
                    { source: "email_address",  target: "email",             status: "mapped"   },
                    { source: "phone_number",   target: "phone",             status: "mapped"   },
                    { source: "purchase_date",  target: "transaction_date",  status: "mapped"   },
                    { source: "item_sku",       target: "product_sku",       status: "mapped"   },
                    { source: "store_location", target: "???",               status: "unmapped" },
                    { source: "loyalty_points", target: "???",               status: "pending"  },
                  ].map((field, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50">
                      <code className="text-xs bg-gray-100 px-2 py-0.5 rounded font-mono flex-1">{field.source}</code>
                      <ChevronRight size={13} className="text-gray-300 flex-shrink-0" />
                      <code className={clsx("text-xs px-2 py-0.5 rounded font-mono flex-1",
                        field.status === "mapped"   ? "bg-emerald-50 text-emerald-700" :
                        field.status === "unmapped" ? "bg-red-50 text-red-500" :
                        "bg-amber-50 text-amber-600")}>{field.target}</code>
                      <span className={clsx("badge text-2xs flex-shrink-0",
                        field.status === "mapped"   ? "badge-green"  :
                        field.status === "unmapped" ? "badge-red"    : "badge-yellow")}>
                        {field.status}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertTriangle size={13} className="text-amber-500" />
                  <p className="text-xs text-amber-700">2 fields need mapping before proceeding to identity resolution</p>
                </div>
              </div>
            )}

            {onboardStep === 4 && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "Records Submitted", value: "1.84M",  status: "complete" },
                    { label: "Matched",           value: "1.58M",  status: "complete" },
                    { label: "Match Rate",         value: "85.9%",  status: "complete" },
                  ].map((m, i) => (
                    <div key={i} className="bg-emerald-50 rounded-lg p-3 text-center">
                      <p className="text-lg font-bold text-emerald-700 tabular">{m.value}</p>
                      <p className="text-2xs text-slate">{m.label}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {[
                    { label: "Email resolution",   pct: 78, status: "complete" },
                    { label: "Phone resolution",   pct: 62, status: "complete" },
                    { label: "Address matching",   pct: 54, status: "complete" },
                    { label: "Intent overlay",     pct: 100, status: "running" },
                    { label: "Lapse scoring",      pct: 45, status: "running"  },
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-gray-800">{item.label}</span>
                        <div className="flex items-center gap-1.5">
                          {item.status === "running" && <RefreshCw size={10} className="text-blue animate-spin" />}
                          <span className="text-xs font-medium text-slate tabular">{item.pct}%</span>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={clsx("h-1.5 rounded-full transition-all duration-700",
                          item.status === "running" ? "bg-blue" : "bg-emerald-500")}
                          style={{ width: `${item.pct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {onboardStep === 5 && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle size={28} className="text-emerald-500" />
                  </div>
                  <h3 className="font-semibold text-emerald-800 mb-1">Onboarding Complete!</h3>
                  <p className="text-xs text-emerald-700">Partner data is live and audiences are ready for activation</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button className="btn-primary btn-sm justify-center gap-2"><Zap size={13} /> Build First Audience</button>
                  <button className="btn-secondary btn-sm justify-center gap-2"><Database size={13} /> Open Clean Room</button>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => setOnboardStep(s => Math.max(1, s-1))} disabled={onboardStep === 1} className="btn-secondary btn-sm">
                Back
              </button>
              <button onClick={() => setOnboardStep(s => Math.min(5, s+1))} disabled={onboardStep === 5} className="btn-primary btn-sm">
                {onboardStep === 4 ? "Complete Onboarding" : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Data Health ── */}
      {tab === "health" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {HEALTH_METRICS.map((h, i) => (
              <div key={i} className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-navy">{h.retailer}</h3>
                  <span className={clsx("badge", h.issues === 0 ? "badge-green" : "badge-yellow")}>
                    {h.issues === 0 ? "Healthy" : `${h.issues} issues`}
                  </span>
                </div>
                <div className="space-y-3">
                  {[
                    { label: "Match Rate",    value: h.matchRate,    suffix: "%", threshold: 85 },
                    { label: "Freshness",     value: h.freshness,    suffix: "%", threshold: 90 },
                    { label: "Completeness",  value: h.completeness, suffix: "%", threshold: 85 },
                  ].map((m, j) => (
                    <div key={j}>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs text-slate">{m.label}</span>
                        <span className={clsx("text-xs font-semibold tabular",
                          m.value >= m.threshold ? "text-emerald-600" : "text-amber-600")}>
                          {m.value}{m.suffix}
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={clsx("h-1.5 rounded-full", m.value >= m.threshold ? "bg-emerald-500" : "bg-amber-400")}
                          style={{ width: `${m.value}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-slate">{(h.records/1_000_000).toFixed(1)}M records</span>
                  <button className="btn-ghost btn-sm flex items-center gap-1 text-xs"><RefreshCw size={11} /> Refresh</button>
                </div>
              </div>
            ))}
          </div>

          {/* Usage & billing */}
          <div className="card">
            <h3 className="text-sm font-semibold text-navy mb-3">Usage & Billing MTD</h3>
            <table className="table-base">
              <thead><tr><th>Partner</th><th>API Calls</th><th>Records Processed</th><th>Audiences Built</th><th>Clean Room Queries</th><th>Platform Fee</th></tr></thead>
              <tbody>
                {[
                  { name: "Midwest Hardware",  api: "48.2K", records: "2.84M", audiences: 12, queries: 84,  fee: "$42,800"  },
                  { name: "FreshMart Grocery", api: "124.8K",records: "5.12M", audiences: 24, queries: 142, fee: "$98,400"  },
                  { name: "StyleNow Apparel",  api: "31.4K", records: "1.67M", audiences: 8,  queries: 52,  fee: "$28,200"  },
                ].map((row, i) => (
                  <tr key={i}>
                    <td className="font-medium text-gray-800">{row.name}</td>
                    <td className="tabular">{row.api}</td>
                    <td className="tabular">{row.records}</td>
                    <td className="tabular">{row.audiences}</td>
                    <td className="tabular">{row.queries}</td>
                    <td className="tabular font-semibold text-navy">{row.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── API Docs ── */}
      {tab === "api" && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { icon: Key,          label: "Authentication",  desc: "OAuth 2.0 bearer tokens. Rate limit: 1K req/min standard, 10K enterprise." },
              { icon: BookOpen,     label: "Documentation",   desc: "Full OpenAPI spec available. SDKs for Python, Node.js, and Java."          },
              { icon: HeadphonesIcon,label: "Support",        desc: "Dedicated integration engineer for enterprise partners. SLA: 4h response."  },
            ].map((item, i) => (
              <div key={i} className="card flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-navy/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={15} className="text-navy" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-navy">{item.label}</p>
                  <p className="text-xs text-slate mt-0.5 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-navy">API Reference</h3>
              <div className="flex gap-1">
                {["All", "Identity", "Audiences", "Campaigns", "Intelligence", "Catalog"].map(cat => (
                  <button key={cat} onClick={() => setApiCategory(cat)}
                    className={clsx("btn btn-sm", apiCategory === cat ? "btn-primary" : "btn-secondary")}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              {filteredEndpoints.map((ep, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                  <span className={clsx("badge text-2xs font-mono w-12 justify-center flex-shrink-0", METHOD_COLORS[ep.method])}>
                    {ep.method}
                  </span>
                  <code className="text-xs font-mono text-navy flex-shrink-0">{ep.path}</code>
                  <span className="text-xs text-slate flex-1">{ep.desc}</span>
                  <span className="badge badge-slate text-2xs flex-shrink-0">{ep.category}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}